import { useMemo } from "react";

import { resolverEmpresaProducto } from "../../utils";

const MES_LABELS = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

// Período de una venta desde mes_factura ("MM-YYYY" → "MES/AA", ej. "04-2026" → "ABR/26").
// Devuelve "" si no hay mes_factura, o el texto crudo si no matchea el formato.
export const periodoLabel = (mesFactura) => {
  const s = (mesFactura || "").trim();
  const m = s.match(/^(\d{1,2})-(\d{4})$/);
  if (m) {
    const idx = parseInt(m[1], 10) - 1;
    return `${MES_LABELS[idx] || m[1]}/${m[2].slice(-2)}`;
  }
  return s;
};

// Etiqueta de un "consumo" (una fila `sales`): "[<campana> · ]<MES>/<AA>".
export const consumoLabel = (sale) => {
  const periodo = periodoLabel(sale?.mes_factura);
  const campana = (sale?.campana || "").trim();
  if (campana && periodo) return `${campana} · ${periodo}`;
  if (campana) return campana;
  return periodo || "(sin período)";
};

// Clave estable de producto: por client_id (id, regla 10.7) y solo si no hay,
// por el texto del producto resuelto (fallback).
const productoKey = (sale, producto) =>
  sale?.client_id != null ? `c${sale.client_id}` : `t:${(producto || "").toLowerCase()}`;

// Deriva las claves de embudo (empresa + productoKey) de un item. Permite al
// consumidor sincronizar los tres dropdowns al seleccionar por fuera (ej. un chip):
// la selección es SIEMPRE { empresa, productoKey, saleId }.
export const clavesEmbudo = (item, clientesById) => {
  const { empresa, producto } = resolverEmpresaProducto(item, clientesById);
  return { empresa, productoKey: productoKey(item, producto) };
};

/**
 * Embudo CONTROLADO Empresa → Producto → Consumo, compartido entre la vista interna
 * y la externa. NO tiene estado propio: la fuente de verdad ÚNICA vive en el
 * consumidor, que pasa { selectedEmpresa, selectedProductoKey, selectedSaleId } y
 * recibe los cambios por onEmpresaChange / onProductoChange / onSelectSale. Así los
 * dropdowns y la unidad seleccionada nunca pueden discrepar (arregla S4).
 *
 * `ventas`: items ya filtrados por el consumidor. Cada item necesita { id, client_id,
 * campana, mes_factura, edificiostotales, cliente }.
 * `conReporteIds`: ids que se marcan con 📷 en el dropdown Consumo (unidades con
 * reporte iniciado). `compact`: variante inline (form-select-sm) para toolbars.
 */
const EmbudoVentaSelector = ({
  ventas,
  clientesById,
  selectedEmpresa,
  selectedProductoKey,
  selectedSaleId,
  onEmpresaChange,
  onProductoChange,
  onSelectSale,
  conReporteIds = [],
  compact = false,
}) => {
  // Estructura anidada: empresa → productos → ventas.
  const empresas = useMemo(() => {
    const mapa = new Map();
    ventas.forEach((v) => {
      const { empresa, producto } = resolverEmpresaProducto(v, clientesById);
      if (!mapa.has(empresa)) mapa.set(empresa, { empresa, productos: new Map() });
      const grupo = mapa.get(empresa);
      const key = productoKey(v, producto);
      if (!grupo.productos.has(key)) grupo.productos.set(key, { key, producto, ventas: [] });
      grupo.productos.get(key).ventas.push(v);
    });
    return mapa;
  }, [ventas, clientesById]);

  const empresaOptions = useMemo(() => [...empresas.keys()].sort((a, b) => a.localeCompare(b, "es")), [empresas]);

  const productoOptions = useMemo(() => {
    const grupo = selectedEmpresa ? empresas.get(selectedEmpresa) : null;
    if (!grupo) return [];
    return [...grupo.productos.values()].sort((a, b) => (a.producto || "").localeCompare(b.producto || "", "es"));
  }, [empresas, selectedEmpresa]);

  const consumoOptions = useMemo(() => {
    const grupo = selectedEmpresa ? empresas.get(selectedEmpresa) : null;
    const prod = grupo && selectedProductoKey ? grupo.productos.get(selectedProductoKey) : null;
    if (!prod) return [];
    return [...prod.ventas].sort((a, b) => (a.mes_factura || "").localeCompare(b.mes_factura || ""));
  }, [empresas, selectedEmpresa, selectedProductoKey]);

  // El value del consumo solo coincide si el id seleccionado pertenece a este producto.
  // Comparación por string: el id puede ser numérico (fila `sales` cruda, vista externa)
  // o compuesto (unidad reportable "s{id}" / "c{contrato}:{periodo}", vista interna).
  const consumoValue = consumoOptions.some((v) => String(v.id) === String(selectedSaleId))
    ? String(selectedSaleId)
    : "";

  const conReporte = useMemo(() => new Set((conReporteIds || []).map(String)), [conReporteIds]);

  const selectClass = compact ? "form-select form-select-sm" : "form-select";

  const empresaSelect = (
    <select
      className={selectClass}
      style={compact ? { width: 160 } : undefined}
      value={selectedEmpresa || ""}
      onChange={(e) => onEmpresaChange(e.target.value)}
    >
      <option value="">{compact ? "Empresa" : "-- Seleccione empresa --"}</option>
      {empresaOptions.map((empresa) => (
        <option key={empresa} value={empresa}>
          {empresa}
        </option>
      ))}
    </select>
  );

  const productoSelect = (
    <select
      className={selectClass}
      style={compact ? { width: 160 } : undefined}
      value={selectedProductoKey || ""}
      onChange={(e) => onProductoChange(e.target.value)}
      disabled={!selectedEmpresa}
    >
      <option value="">{compact ? "Producto" : "-- Seleccione producto --"}</option>
      {productoOptions.map((p) => (
        <option key={p.key} value={p.key}>
          {p.producto || "(sin producto)"}
        </option>
      ))}
    </select>
  );

  const consumoSelect = (
    <select
      className={selectClass}
      style={compact ? { width: 220 } : undefined}
      value={consumoValue}
      onChange={(e) => onSelectSale(e.target.value)}
      disabled={!selectedProductoKey}
    >
      <option value="">{compact ? "Consumo" : "-- Seleccione consumo --"}</option>
      {consumoOptions.map((v) => (
        <option key={v.id} value={v.id}>
          {conReporte.has(String(v.id)) ? "📷 " : ""}
          {consumoLabel(v)}
          {` (${v.edificiostotales} edif.)`}
        </option>
      ))}
    </select>
  );

  if (compact) {
    return (
      <div className="d-flex align-items-center gap-2">
        {empresaSelect}
        {productoSelect}
        {consumoSelect}
      </div>
    );
  }

  return (
    <div className="row g-2">
      <div className="col-md-4">
        <label className="form-label fw-semibold">Empresa</label>
        {empresaSelect}
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Producto</label>
        {productoSelect}
      </div>
      <div className="col-md-4">
        <label className="form-label fw-semibold">Consumo</label>
        {consumoSelect}
      </div>
    </div>
  );
};

export default EmbudoVentaSelector;
